import { useRef, useState, type ReactNode } from "react";
import type { Actor, FieldDef, FieldValue, Kind, Node, StatusDef } from "@melo/shared";
import { DISCIPLINE_LABELS, formatFieldValue } from "@melo/shared/client";
import { store, useTables } from "../lib/store";
import { agents, formatDate, people } from "../lib/select";
import { assign, setStatus, updateNode } from "../lib/actions";
import { isContractorIn } from "../lib/access";
import { Avatar, MenuList, Popover } from "./primitives";
import { Icon } from "./icons";

const CATEGORY_TONE: Record<StatusDef["category"], string> = { todo: "var(--deco)", active: "var(--primary-2)", review: "var(--warn)", done: "var(--ok)" };

export function StatusDot({ status }: { status: StatusDef | undefined }) {
  const tone = status ? CATEGORY_TONE[status.category] : "var(--deco)";
  const filled = status?.category === "done" || status?.category === "review";
  return <span className="d" style={{ background: filled ? tone : "transparent", boxShadow: `inset 0 0 0 1.5px ${tone}` }} aria-hidden="true" />;
}

export function StatusChip({ kind, status }: { kind: Kind | undefined; status: string }) {
  const def = kind?.statuses.find((s) => s.key === status);
  return (
    <span className="chip">
      <StatusDot status={def} />
      {def?.label ?? status}
    </span>
  );
}

/** A button that opens a menu. The trigger is whatever you pass as children. */
export function MenuButton({ children, className = "btn sm", label, items, searchable, multi, align, width, onCreate, createLabel, placeholder }: { children: ReactNode; className?: string; label: string; items: () => import("./primitives").MenuItem[]; searchable?: boolean; multi?: boolean; align?: "start" | "end"; width?: number; onCreate?: (text: string) => void; createLabel?: string; placeholder?: string }) {
  const ref = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  return (
    <>
      <button ref={ref} type="button" className={className} aria-haspopup="listbox" aria-expanded={open} aria-label={label} title={label} onClick={() => setOpen((o) => !o)}>
        {children}
      </button>
      {open && (
        <Popover anchor={ref.current} onClose={() => setOpen(false)} align={align} width={width}>
          <MenuList items={items()} searchable={searchable} multi={multi} onPick={() => setOpen(false)} onCreate={onCreate} createLabel={createLabel} placeholder={placeholder} />
        </Popover>
      )}
    </>
  );
}

export function StatusPicker({ node, kind }: { node: Node; kind: Kind | undefined }) {
  const def = kind?.statuses.find((s) => s.key === node.status);
  // A contractor's own task is closed by its reviewer, so "done" statuses are not offered to them.
  const hideDone = isContractorIn(node.spaceId) && node.assignees.some((a) => a.actorId === store.me?.id);
  return (
    <MenuButton
      label="Change status"
      className="chip"
      items={() =>
        (kind?.statuses ?? []).filter((s) => !hideDone || s.category !== "done").map((s) => ({
          id: s.key,
          label: s.label,
          icon: (
            <span className="chip" style={{ border: 0, background: "none", padding: 0 }}>
              <StatusDot status={s} />
            </span>
          ),
          checked: s.key === node.status,
          run: () => s.key !== node.status && void setStatus(node, s.key),
        }))
      }
    >
      <StatusDot status={def} />
      {def?.label ?? node.status}
    </MenuButton>
  );
}

/** Owners do the work; a reviewer (usually QA) approves it. Each picker edits one role and keeps the other. */
export function AssigneePicker({ node, compact, role = "owner" }: { node: Node; compact?: boolean; role?: "owner" | "reviewer" }) {
  const t = useTables();
  const ids = node.assignees.filter((a) => a.role === role).map((a) => a.actorId);
  const toggle = (a: Actor) => {
    const cur = store.t.nodes.get(node.id) ?? node;
    const mine = cur.assignees.filter((x) => x.role === role).map((x) => x.actorId);
    const others = cur.assignees.filter((x) => x.role !== role && x.actorId !== a.id);
    const nextMine = mine.includes(a.id) ? mine.filter((x) => x !== a.id) : role === "reviewer" ? [a.id] : [...mine, a.id];
    void assign(cur, [...nextMine.map((actorId) => ({ actorId, role })), ...others]);
  };
  const members = new Set([...t.members.values()].filter((m) => m.spaceId === node.spaceId).map((m) => m.actorId));
  const list = [...people(t), ...agents(t)].filter((a) => members.has(a.id) || a.role === "admin" || a.type === "agent").filter((a) => role === "owner" || (a.type === "person" && a.role !== "contractor"));
  return (
    <MenuButton
      label={role === "reviewer" ? "Change reviewer" : "Change assignees"}
      className={compact ? "btn ghost sm" : "btn sm"}
      searchable
      multi
      items={() =>
        list.map((a) => ({
          id: a.id,
          label: a.type === "agent" ? `${a.name} (agent)` : a.name,
          icon: <Avatar actor={a} size="sm" />,
          checked: (store.t.nodes.get(node.id)?.assignees ?? node.assignees).some((x) => x.actorId === a.id && x.role === role),
          run: () => toggle(a),
        }))
      }
    >
      {ids.length ? (
        <span className="avs">
          {ids.map((id) => (
            <Avatar key={id} actor={t.actors.get(id)} size="sm" />
          ))}
        </span>
      ) : (
        <>
          <Icon name="user" /> {compact ? "" : role === "reviewer" ? "Add reviewer" : "Assign"}
        </>
      )}
    </MenuButton>
  );
}

/** Edits one typed field in place. Saves on change for pickers and on blur for text. */
export function FieldEditor({ node, def }: { node: Node; def: FieldDef }) {
  const value = node.fields[def.key];
  const save = (v: FieldValue) => void updateNode(node, { fields: { [def.key]: v === "" ? null : v } });
  const t = useTables();
  switch (def.type) {
    case "select":
      return (
        <MenuButton
          label={`Set ${def.label}`}
          className="chip"
          items={() => [
            ...(def.options ?? []).map((o) => ({ id: o, label: optionLabel(def, o), checked: value === o, run: () => save(o) })),
            ...(value ? [{ id: "__clear", label: "Clear", run: () => save(null) }] : []),
          ]}
        >
          {value ? optionLabel(def, String(value)) : <span className="muted">None</span>}
        </MenuButton>
      );
    case "multiselect": {
      const cur = Array.isArray(value) ? value : [];
      return (
        <MenuButton
          label={`Set ${def.label}`}
          className="chip"
          multi
          items={() =>
            (def.options ?? []).map((o) => ({
              id: o,
              label: optionLabel(def, o),
              checked: cur.includes(o),
              run: () => {
                const latest = store.t.nodes.get(node.id)?.fields[def.key];
                const list = Array.isArray(latest) ? latest : [];
                save(list.includes(o) ? list.filter((x) => x !== o) : [...list, o]);
              },
            }))
          }
        >
          {cur.length ? cur.map((o) => optionLabel(def, o)).join(", ") : <span className="muted">Empty</span>}
        </MenuButton>
      );
    }
    case "checkbox":
      return (
        <label className="check">
          <input type="checkbox" checked={value === true} onChange={(e) => save(e.target.checked)} />
          {value === true ? "Yes" : "No"}
        </label>
      );
    case "actor": {
      const actor = typeof value === "string" ? t.actors.get(value) : undefined;
      return (
        <MenuButton
          label={`Set ${def.label}`}
          className="chip"
          searchable
          items={() => [...people(store.t), ...agents(store.t)].map((a) => ({ id: a.id, label: a.name, icon: <Avatar actor={a} size="sm" />, checked: value === a.id, run: () => save(a.id) }))}
        >
          {actor ? actor.name : <span className="muted">Empty</span>}
        </MenuButton>
      );
    }
    case "date":
      return <DateInput label={def.label} value={typeof value === "string" ? value : null} onSave={(v) => save(v)} />;
    default:
      return <InlineInput key={`${node.id}:${def.key}:${String(value ?? "")}`} type={def.type === "number" ? "number" : def.type === "url" ? "url" : "text"} value={value == null ? "" : String(value)} placeholder="Empty" label={def.label} unit={def.unit} onSave={(v) => save(def.type === "number" ? (v === "" ? null : Number(v)) : v)} />;
  }
}

export function InlineInput({ value, onSave, type = "text", placeholder, unit, className = "inline-input", label }: { value: string; onSave: (v: string) => void; type?: string; placeholder?: string; unit?: string; className?: string; label?: string }) {
  const [v, setV] = useState(value);
  const commit = () => v.trim() !== value && onSave(v.trim());
  return (
    <span className="inline-wrap">
      <input
        className={className}
        type={type}
        value={v}
        placeholder={placeholder}
        aria-label={label}
        onChange={(e) => setV(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") (e.target as HTMLInputElement).blur();
          if (e.key === "Escape") {
            setV(value);
            (e.target as HTMLInputElement).blur();
          }
        }}
      />
      {unit && v !== "" && <span className="muted unit">{unit}</span>}
    </span>
  );
}

/** Shows a date as "Sep 30" (or "Set date"); click to pick. Clearing is one click too. */
export function DateInput({ value, onSave, label, placeholder = "Set date", className }: { value: string | null; onSave: (v: string | null) => void; label: string; placeholder?: string; className?: string }) {
  const input = useRef<HTMLInputElement>(null);
  return (
    <span className="date-input">
      <button
        type="button"
        className={`${className ?? "chip date-chip"}${value ? "" : " is-empty"}`}
        aria-label={`${label}: ${value ? formatDate(value) : "not set"}. Change`}
        onClick={() => {
          const el = input.current;
          if (!el) return;
          try {
            el.showPicker();
          } catch {
            el.focus();
          }
        }}
      >
        <Icon name="calendar" />
        {value ? formatDate(value) : placeholder}
      </button>
      <input ref={input} type="date" tabIndex={-1} aria-hidden="true" className="date-native" value={value ?? ""} onChange={(e) => onSave(e.target.value || null)} />
      {value && (
        <button type="button" className="date-clear" aria-label={`Clear ${label}`} onClick={() => onSave(null)}>
          <Icon name="x" />
        </button>
      )}
    </span>
  );
}

/** How an option reads: disciplines by their names, anything else with a capital first letter. */
export function optionLabel(def: Pick<FieldDef, "key">, value: string): string {
  if (def.key === "discipline" || def.key === "owner_discipline") return DISCIPLINE_LABELS[value] ?? value;
  return value ? value[0]!.toUpperCase() + value.slice(1) : value;
}

export function fieldText(def: FieldDef, value: FieldValue | undefined, actors: Map<string, Actor>): string {
  if (def.type === "actor" && typeof value === "string") return actors.get(value)?.name ?? "";
  if (def.type === "select" && typeof value === "string") return optionLabel(def, value);
  if (def.type === "multiselect" && Array.isArray(value)) return value.map((v) => optionLabel(def, v)).join(", ");
  return formatFieldValue(def, value);
}
